import { useState, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { TareaArtifact } from '@/lib/types'
import { supabase } from '@/lib/supabase'
import { IconFile, IconEdit, IconGlobe, IconUpload, IconX, IconCheck } from '@/components/icons/Icons'

type TabId = 'archivo' | 'canvas' | 'google_doc'

interface Props {
  isOpen: boolean
  onClose: () => void
  onAdd: (artifact: TareaArtifact) => void
  cotizacionId: number | null
  tareaIdx: number
}

const ACCEPTED = '.pdf,.md,.txt,.html,.docx'
const DOC_ID_REGEX = /docs\.google\.com\/document\/d\/([a-zA-Z0-9_-]+)/

function extractGoogleDocId(url: string): string | null {
  const match = url.match(DOC_ID_REGEX)
  return match ? match[1] : null
}

export default function ArtifactUploader({ isOpen, onClose, onAdd, cotizacionId, tareaIdx }: Props) {
  const [activeTab, setActiveTab] = useState<TabId>('archivo')

  // ── Archivo state ──
  const [file, setFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // ── Canvas state ──
  const [canvasName, setCanvasName] = useState('')
  const [canvasContent, setCanvasContent] = useState('')

  // ── Google Doc state ──
  const [docUrl, setDocUrl] = useState('')
  const [docName, setDocName] = useState('')
  const [docIdPreview, setDocIdPreview] = useState<string | null>(null)

  const overlayRef = useRef<HTMLDivElement>(null)

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setActiveTab('archivo')
      setFile(null)
      setFileName('')
      setUploading(false)
      setUploadError(null)
      setIsDragOver(false)
      setCanvasName('')
      setCanvasContent('')
      setDocUrl('')
      setDocName('')
      setDocIdPreview(null)
    }
  }, [isOpen])

  // Auto-extract Google Doc ID
  useEffect(() => {
    const id = extractGoogleDocId(docUrl)
    setDocIdPreview(id)
  }, [docUrl])

  // Close on Escape
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  // ── File handling ──
  const handleFileSelect = useCallback((selected: File) => {
    setFile(selected)
    setFileName(selected.name.replace(/\.[^.]+$/, ''))
    setUploadError(null)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const dropped = e.dataTransfer.files[0]
    if (dropped) handleFileSelect(dropped)
  }, [handleFileSelect])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false)
  }, [])

  // ── Upload archivo ──
  const handleUpload = async () => {
    if (!file || !fileName.trim()) return
    setUploading(true)
    setUploadError(null)

    try {
      const id = crypto.randomUUID()
      const path = `${cotizacionId || 'draft'}/${tareaIdx}/${id}/${file.name}`
      const { error } = await supabase.storage
        .from('cotizacion-artifacts')
        .upload(path, file, { contentType: file.type, upsert: false })
      if (error) throw error

      const { data: urlData } = supabase.storage
        .from('cotizacion-artifacts')
        .getPublicUrl(path)

      const artifact: TareaArtifact = {
        id,
        tipo: 'archivo',
        nombre: fileName.trim(),
        url: urlData.publicUrl,
        storage_path: path,
        mime_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
        created_at: new Date().toISOString(),
      }
      onAdd(artifact)
      onClose()
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Error al subir archivo')
    } finally {
      setUploading(false)
    }
  }

  // ── Save canvas ──
  const handleSaveCanvas = () => {
    if (!canvasName.trim() || !canvasContent.trim()) return

    const artifact: TareaArtifact = {
      id: crypto.randomUUID(),
      tipo: 'canvas',
      nombre: canvasName.trim(),
      contenido_raw: canvasContent,
      created_at: new Date().toISOString(),
    }
    onAdd(artifact)
    onClose()
  }

  // ── Link Google Doc ──
  const handleLinkDoc = () => {
    if (!docUrl.trim() || !docName.trim() || !docIdPreview) return

    const artifact: TareaArtifact = {
      id: crypto.randomUUID(),
      tipo: 'google_doc',
      nombre: docName.trim(),
      google_doc_id: docIdPreview,
      google_doc_url: docUrl.trim(),
      created_at: new Date().toISOString(),
    }
    onAdd(artifact)
    onClose()
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose()
  }

  if (!isOpen) return null

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'archivo',    label: 'Archivo',    icon: <IconFile size={14} /> },
    { id: 'canvas',     label: 'Canvas',     icon: <IconEdit size={14} /> },
    { id: 'google_doc', label: 'Google Doc', icon: <IconGlobe size={14} /> },
  ]

  return createPortal(
    <div
      className="modal-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{ display: 'flex' }}
    >
      <div className="modal artifact-uploader">
        {/* Header */}
        <div className="modal-header">
          <h3>Agregar Artifact</h3>
          <button className="modal-close" onClick={onClose} type="button">
            <IconX size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="artifact-uploader__tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`artifact-uploader__tab ${activeTab === tab.id ? 'artifact-uploader__tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* ── Tab: Archivo ── */}
          {activeTab === 'archivo' && (
            <div className="artifact-uploader__panel">
              <div
                className={`artifact-uploader__dropzone ${isDragOver ? 'artifact-uploader__dropzone--active' : ''} ${file ? 'artifact-uploader__dropzone--has-file' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') fileInputRef.current?.click() }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED}
                  className="artifact-uploader__file-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) handleFileSelect(f)
                  }}
                />
                {file ? (
                  <div className="artifact-uploader__file-preview">
                    <IconCheck size={20} />
                    <span>{file.name}</span>
                    <span className="artifact-uploader__file-size">
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                ) : (
                  <div className="artifact-uploader__dropzone-hint">
                    <IconUpload size={24} />
                    <span>Arrastra un archivo aquí o haz clic para seleccionar</span>
                    <span className="artifact-uploader__dropzone-formats">
                      PDF, Markdown, TXT, HTML, DOCX
                    </span>
                  </div>
                )}
              </div>

              <label className="artifact-uploader__label">
                Nombre del artifact
                <input
                  type="text"
                  className="artifact-uploader__input"
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  placeholder="Nombre del archivo"
                />
              </label>

              {uploadError && (
                <div className="artifact-uploader__error">{uploadError}</div>
              )}

              <div className="artifact-uploader__actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpload}
                  disabled={!file || !fileName.trim() || uploading}
                >
                  {uploading ? 'Subiendo…' : 'Subir archivo'}
                </button>
              </div>
            </div>
          )}

          {/* ── Tab: Canvas ── */}
          {activeTab === 'canvas' && (
            <div className="artifact-uploader__panel">
              <label className="artifact-uploader__label">
                Nombre
                <input
                  type="text"
                  className="artifact-uploader__input"
                  value={canvasName}
                  onChange={(e) => setCanvasName(e.target.value)}
                  placeholder="Ej: Especificación técnica"
                />
              </label>

              <label className="artifact-uploader__label">
                Contenido
                <textarea
                  className="artifact-uploader__textarea"
                  value={canvasContent}
                  onChange={(e) => setCanvasContent(e.target.value)}
                  placeholder="Pega aquí el contenido del artifact..."
                  rows={12}
                />
              </label>

              <div className="artifact-uploader__actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveCanvas}
                  disabled={!canvasName.trim() || !canvasContent.trim()}
                >
                  Guardar
                </button>
              </div>
            </div>
          )}

          {/* ── Tab: Google Doc ── */}
          {activeTab === 'google_doc' && (
            <div className="artifact-uploader__panel">
              <label className="artifact-uploader__label">
                URL del Google Doc
                <input
                  type="url"
                  className="artifact-uploader__input"
                  value={docUrl}
                  onChange={(e) => setDocUrl(e.target.value)}
                  placeholder="https://docs.google.com/document/d/..."
                />
              </label>

              {docUrl && (
                <div className={`artifact-uploader__doc-preview ${docIdPreview ? 'artifact-uploader__doc-preview--valid' : 'artifact-uploader__doc-preview--invalid'}`}>
                  {docIdPreview
                    ? <>
                        <IconCheck size={14} />
                        <span>Doc ID: <code>{docIdPreview.slice(0, 20)}…</code></span>
                      </>
                    : <span>URL no válida — debe ser docs.google.com/document/d/...</span>
                  }
                </div>
              )}

              <label className="artifact-uploader__label">
                Nombre
                <input
                  type="text"
                  className="artifact-uploader__input"
                  value={docName}
                  onChange={(e) => setDocName(e.target.value)}
                  placeholder="Ej: Brief del proyecto"
                />
              </label>

              <div className="artifact-uploader__actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleLinkDoc}
                  disabled={!docUrl.trim() || !docName.trim() || !docIdPreview}
                >
                  Vincular
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
